import React from 'react';

/**
 * Componente de abas (tabs) para navegação entre diferentes seções
 * @param {Object} props - Propriedades do componente
 * @param {string} props.value - Valor da aba selecionada atualmente
 * @param {Function} props.onChange - Função chamada quando uma aba é selecionada
 * @param {ReactNode} props.children - TabItem elements
 * @param {string} props.className - Classes CSS adicionais
 * @returns {JSX.Element} Componente de abas
 */
const Tabs = ({ value, onChange, children, className = '' }) => {
  // Filtrar apenas os elementos TabItem válidos
  const tabItems = React.Children.toArray(children).filter(
    child => React.isValidElement(child) && child.type.displayName === 'TabItem'
  );

  return (
    <div className={`border-b border-gray-200 ${className}`}>
      <nav className="-mb-px flex space-x-8">
        {tabItems.map((tabItem) => {
          const isActive = tabItem.props.value === value;
          
          return (
            <button
              key={tabItem.props.value}
              onClick={() => onChange(tabItem.props.value)}
              className={`
                flex items-center whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium
                ${isActive
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
              aria-current={isActive ? 'page' : undefined}
            >
              {tabItem.props.icon && (
                <span className={`mr-2 ${isActive ? 'text-blue-500' : 'text-gray-400'}`}>
                  {tabItem.props.icon}
                </span>
              )}
              {tabItem.props.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

/**
 * Item de aba individual
 * @param {Object} props - Propriedades do componente
 * @param {string} props.value - Valor único da aba
 * @param {string} props.label - Texto exibido na aba
 * @param {ReactNode} props.icon - Ícone opcional para a aba
 * @returns {null} Este componente não renderiza nada diretamente
 */
const TabItem = ({ value, label, icon }) => {
  return null; // Este componente é apenas para configuração
};

// Definir displayName para identificação no componente pai
TabItem.displayName = 'TabItem';

export { Tabs, TabItem }; 